import { useEffect, useRef, useState } from 'react';
import { createMorphProgram, rgb01, type MorphProgram } from './morphProgram';
import { DEFAULT_PARTS_GRID } from './parts';

/**
 * The Morph Resolve surface: one WebGL1 canvas running the lab's shader, driven by a phase prop
 * instead of the lab's buttons.
 *
 * ## The timeline it implements
 *
 * A generation is a wait of unknown length followed by a resolve of known length — two separate
 * clocks, exactly the lab's model. `'band'` is the wait (p pinned at 0, the hold eased up over 500ms
 * so the card thickens out of nothing); `'morph-out'` melts existing content INTO that band over
 * 500ms and then behaves as `'wobble'` on its own, so the host can set it once at rewrite start and
 * forget it; `'wobble'` is the band holding; `'resolve'` drives p 0→1 over `resolveMs` with the lab's
 * hold release — `hold = 1 - sstep(0.02, 0.55, p)` — let go across the FRONT of the resolve rather
 * than cut at its first frame, so the content grows out of the sites instead of the two states
 * swapping. The band never has to know how long the wait will run.
 *
 * ## Rules this component lives by
 *
 * - **Compositor-only outside the GL.** No React state is touched per frame: the rAF writes uniforms
 *   and draws, callbacks are read through refs, and the only setState in the file is the one-shot
 *   fallback flip. React renders this component a handful of times per generation, not per frame.
 * - **The backing store is FIXED at 1024x576.** The CSS size follows the `width`/`height` props; a
 *   resize is a style write, never a drawing-buffer realloc — reallocating mid-band would flush the
 *   context and hitch the very frame a layout change already made expensive.
 * - **The clock is the wall clock.** The band is stateless in time (`uTime` is `performance.now()`),
 *   so pausing on `visibilitychange` and resuming is just... continuing. No catch-up bookkeeping. A
 *   resolve that elapsed while hidden lands at p=1 on the first visible frame, which is the truth.
 * - **At most ONE GL context on /deck.** One MorphCanvas exists at a time by design — the stage
 *   unmounts it after the resolve fades — so this canvas is the route's only context and never
 *   competes with itself for GPU memory. Do not mount several.
 * - Reduced motion is the HOST's job: a host that honours it simply never mounts this component, so
 *   no media query is read here.
 *
 * On context-creation failure or `webglcontextlost` the component renders nothing and reports
 * `onFallback` once — the artifact still appears underneath, just without ceremony.
 */

export type MorphPhase = 'band' | 'morph-out' | 'wobble' | 'resolve' | 'idle';

export interface MorphCanvasProps {
  /** CSS size only — the drawing buffer never follows it (see the fixed-backing-store rule). */
  width: number;
  height: number;
  phase: MorphPhase;
  /** What was on the card before a rewrite; melts into the band during `'morph-out'`. */
  fromTexture?: HTMLCanvasElement | null;
  /** The finished render; sampled only once `'resolve'` starts — never visible before it. */
  toTexture?: HTMLCanvasElement | null;
  /** `partsMapFrom(...)` of `toTexture`, on the default 64x36 grid. Missing → no stagger. */
  partsMap?: { delays: Uint8Array; count: number } | null;
  /** The known-length tail. The tuned product resolve is 2550ms. */
  resolveMs?: number;
  /** Fired once, at p >= 0.98 — the stage starts its fade here, over the already-real pixels. */
  onResolved?: () => void;
  /** Fired once if WebGL is unavailable or the context is lost; the component renders nothing. */
  onFallback?: () => void;
  className?: string;
}

const BACKING_W = 1024;
const BACKING_H = 576;
/** The lab's ease-up: the band thickens out of an empty card over this long. */
const EASE_UP_MS = 500;
/** How long `'morph-out'` takes to melt the old content into the band before acting as wobble. */
const MORPH_OUT_MS = 500;
const DEFAULT_RESOLVE_MS = 2550;

/*
 * The one product preset — the tuned values, which deliberately override the lab's baked `deck`
 * preset (that one went stale as the effect was tuned live). Keys are the lab's slider ids, which
 * are also `morphProgram`'s uniform table keys, so a value can be traced back to its slider.
 */
const TUNED = {
  blur: 156,
  thr: 0.38,
  edge: 0.142,
  stagger: 0.27,
  warp: 10,
  freq: 2.2,
  grain: 2.94,
  grainScale: 3.0,
  core: 1.0,
  sat: 1.02,
  spread: 1.0,
  cover: 1.06,
  sweep: 0.64,
  sweepSpeed: 0.1,
  wave: 0.0,
  sites: 5,
  cellSize: 0.32,
  bandSpeed: 2.35,
} as const;

/** The card while it waits — the lab's "Dark" wait ground. */
const GROUND_WAIT = rgb01('#141416');
/** An artifact's own ground. Deck artifacts are white cards; the resolve hands over to this. */
const GROUND_ARTIFACT = rgb01('#ffffff');
/** Random colour mode's base material tint — per-cluster hues mix over it in the shader. */
const WAIT_COL = rgb01('#c6c6cc');

/** The lab's `sstep`: smoothstep with clamping, used for every ease on the CPU side. */
function sstep(a: number, b: number, x: number): number {
  const u = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return u * u * (3 - 2 * u);
}

export function MorphCanvas({
  width,
  height,
  phase,
  fromTexture,
  toTexture,
  partsMap,
  resolveMs = DEFAULT_RESOLVE_MS,
  onResolved,
  onFallback,
  className,
}: MorphCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const programRef = useRef<MorphProgram | null>(null);
  const rafRef = useRef(0);

  // Latest-value refs, written in the render body (idempotent), so the rAF closure never goes stale
  // and no effect has to re-run just because the host re-rendered with a fresh callback identity.
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const resolveMsRef = useRef(resolveMs);
  resolveMsRef.current = resolveMs;
  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;
  const onFallbackRef = useRef(onFallback);
  onFallbackRef.current = onFallback;

  // The clocks. All wall-clock timestamps; the frame function derives everything else from them.
  const runStartRef = useRef(0); // when this wait began — drives the 500ms ease-up
  const morphOutStartRef = useRef(0); // when the melt began — 500ms later it IS the wobble
  const resolveStartRef = useRef(0); // when the known-length tail began
  const seedRef = useRef(0); // rerolled per run: every wait is a different body
  const baseHueRef = useRef(0); // rerolled per run: Random colour mode's base hue
  const resolvedRef = useRef(false); // onResolved fires once per resolve, from the rAF

  // The single piece of React state: the fallback flip. One transition, never per frame.
  const [dead, setDead] = useState(false);
  const deadRef = useRef(false);

  /* ── the context, created once ─────────────────────────────────────────────────────────────── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || deadRef.current) return;
    const fail = (): void => {
      if (deadRef.current) return;
      deadRef.current = true;
      setDead(true);
      onFallbackRef.current?.();
    };
    const gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false });
    if (!gl) {
      fail();
      return;
    }
    let program: MorphProgram;
    try {
      program = createMorphProgram(gl);
    } catch {
      // A compile/link failure (or an already-lost context) is a device we don't draw on.
      fail();
      return;
    }
    programRef.current = program;
    const onLost = (e: Event): void => {
      // preventDefault signals we won't wait for a restore — the stage falls back instead.
      e.preventDefault();
      fail();
    };
    canvas.addEventListener('webglcontextlost', onLost);
    return () => {
      canvas.removeEventListener('webglcontextlost', onLost);
      programRef.current = null;
      program.dispose();
      // No loseContext() here: under StrictMode the same canvas mounts again immediately, and a
      // deliberately-lost context would come back unusable. The objects above are freed; the context
      // itself goes with the canvas element.
    };
  }, []);

  /* ── phase transitions: clock resets and per-run rerolls ───────────────────────────────────── */
  const prevPhaseRef = useRef<MorphPhase>('idle');
  useEffect(() => {
    const prev = prevPhaseRef.current;
    prevPhaseRef.current = phase;
    const now = performance.now();
    const waiting = phase === 'band' || phase === 'morph-out' || phase === 'wobble';
    // A run starts when a wait begins from rest. band→wobble or morph-out→wobble mid-run keeps the
    // clocks — the band must not visibly reseed just because the host renamed its own state.
    if (waiting && (prev === 'idle' || prev === 'resolve')) {
      runStartRef.current = now;
      seedRef.current = Math.random() * 100; // the lab's per-run reseed range
      baseHueRef.current = Math.random(); // Random mode: the base hue rerolls per run
      resolvedRef.current = false;
      if (phase === 'morph-out') morphOutStartRef.current = now;
    }
    if (phase === 'resolve') {
      resolveStartRef.current = now;
      resolvedRef.current = false;
    }
  }, [phase]);

  /* ── texture slots ─────────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    programRef.current?.uploadFrom(fromTexture ?? null);
  }, [fromTexture, dead]);

  useEffect(() => {
    const program = programRef.current;
    if (!program) return;
    if (phase === 'resolve') {
      program.uploadTo(toTexture ?? null);
      program.uploadParts(partsMap?.delays ?? null, DEFAULT_PARTS_GRID.w, DEFAULT_PARTS_GRID.h);
    } else {
      // Outside the resolve the To slot MUST be blank. During morph-out uGen is 0, so a bound target
      // would start sharpening into view before the resolve was ever asked for; during the band's
      // 500ms ease-up its weight is (1 - hold) > 0 at p=0, so it would flash at band start. The lab
      // never hits either case because it repaints textures per run; here props can arrive early.
      program.uploadTo(null);
      program.uploadParts(null, 1, 1);
    }
  }, [phase, toTexture, partsMap, dead]);

  /* ── the loop: runs only while there is a phase to draw and the tab is visible ─────────────── */
  useEffect(() => {
    if (dead || phase === 'idle') return;

    const frame = (): void => {
      const program = programRef.current;
      if (!program || deadRef.current) return;
      const now = performance.now();
      const ph = phaseRef.current;

      // morph-out is self-terminating: past its 500ms it renders exactly as wobble. Derived from
      // the clock every frame — no timer, no state, nothing to cancel.
      const mo = ph === 'morph-out' ? Math.min(1, (now - morphOutStartRef.current) / MORPH_OUT_MS) : 1;
      const melting = ph === 'morph-out' && mo < 1;

      let p = 0; // uP — the shader smoothsteps it once more, as the lab does
      let hold = 0;
      let gen = 1;
      let groundA = GROUND_WAIT;
      let groundB = GROUND_ARTIFACT;

      if (melting) {
        // The rewrite's opening beat: the old content blurs away (uGen=0 keeps the outgoing term)
        // while the band eases up underneath it and the ground crossfades white → dark.
        gen = 0;
        p = mo;
        hold = sstep(0, 1, mo);
        groundA = GROUND_ARTIFACT;
        groundB = GROUND_WAIT;
      } else if (ph === 'resolve') {
        const raw = Math.min(1, (now - resolveStartRef.current) / resolveMsRef.current);
        // The lab's release: held through the first 2%, let go across the front of the resolve —
        // the sites have to still be there for the content to grow out of, or the states just swap.
        const up = sstep(0, EASE_UP_MS, now - runStartRef.current);
        hold = up * (1 - sstep(0.02, 0.55, raw));
        p = sstep(0, 1, raw); // the lab eases the gen-path scrub before the shader eases again
        if (raw >= 0.98 && !resolvedRef.current) {
          resolvedRef.current = true;
          onResolvedRef.current?.();
        }
      } else {
        // band / wobble / a morph-out that has finished melting: the wait. p stays 0; the hold is
        // full, arriving through the 500ms ease-up so a fresh card thickens instead of popping.
        hold = sstep(0, EASE_UP_MS, now - runStartRef.current);
      }

      program.set1f('p', p);
      program.set1f('hold', hold);
      program.set1f('gen', gen);
      program.set1f('time', now / 1000);
      program.set1f('seed', seedRef.current);
      program.set1f('baseHue', baseHueRef.current);
      program.set1f('multi', 1); // Random colour mode is the product's only mode
      program.set3f('waitCol', WAIT_COL);
      program.set3f('groundA', groundA);
      program.set3f('groundB', groundB);
      program.set1f('blur', TUNED.blur);
      program.set1f('thr', TUNED.thr);
      program.set1f('edge', TUNED.edge);
      program.set1f('stagger', TUNED.stagger);
      program.set1f('warp', TUNED.warp);
      program.set1f('freq', TUNED.freq);
      program.set1f('grain', TUNED.grain);
      program.set1f('grainScale', TUNED.grainScale);
      program.set1f('core', TUNED.core);
      program.set1f('sat', TUNED.sat);
      program.set1f('spread', TUNED.spread);
      program.set1f('cover', TUNED.cover);
      program.set1f('sweep', TUNED.sweep);
      program.set1f('sweepSpeed', TUNED.sweepSpeed);
      program.set1f('wave', TUNED.wave);
      program.set1f('sites', TUNED.sites);
      program.set1f('cellSize', TUNED.cellSize);
      program.set1f('bandSpeed', TUNED.bandSpeed);
      program.draw(BACKING_W, BACKING_H);

      rafRef.current = requestAnimationFrame(frame);
    };

    // Pause with the tab, resume clean: the band is driven by the wall clock, so resuming is just
    // drawing the frame that time says it is. No catch-up, no accumulated-delta bookkeeping.
    const onVisibility = (): void => {
      cancelAnimationFrame(rafRef.current);
      if (!document.hidden) rafRef.current = requestAnimationFrame(frame);
    };
    document.addEventListener('visibilitychange', onVisibility);
    if (!document.hidden) rafRef.current = requestAnimationFrame(frame);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      cancelAnimationFrame(rafRef.current);
    };
  }, [phase, dead]);

  if (dead) return null;

  return (
    <canvas
      ref={canvasRef}
      width={BACKING_W}
      height={BACKING_H}
      className={className}
      // CSS size only. The attributes above are the fixed backing store and never change.
      style={{ width, height, display: 'block' }}
    />
  );
}
